import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import { useSnackbar } from "notistack";
import { useSelector } from "react-redux";
// Hooks
import { useChangeRequestsData } from "@/Washouts/hooks/ChangeRequests/useChangeRequestsData";
// Components
import ChangeRequestTable, { TChangesTableProps } from "../tables/Common/ChangeRequestTable";
// Helpers
import { canDelete, changeRequestSetQueryDataCallback, createErrorToast } from "../tables/Common/ChangeRequestTable/helpers";
// Types
import { IGetChangeRequest, IGetChangeRequestsByCommissionCycle } from "@/Washouts/types/PayrollClosing/queries";
import { RootState } from "@/redux/store";
import ChangeRequestChat from "../ChangeRequestChat";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateChangeRequest } from "@/Washouts/api/WashoutReview/mutations";
import { IChangeRequestUpdateInput, IInput } from "@/Common/api/types/ChangeRequests";
import { getChatAttachmentsPresignedUrl } from "@/Washouts/api/WashoutReview/queries";
import { apiInstance } from "@/services/httpAdapter";
import { set } from "lodash";

interface IChangeRequestFilter {
	escalated?: boolean;
	origin?: "SUPPORT" | "REVIEW";
	userId?: string;
	position?: string;
}

type IChangeRequestTableProps = IChangeRequestFilter & {
	payPeriodId: string;
	locationId: string;
	commissionCycleId?: string;
	disableChat?: boolean;
	skeletonRows?: string[];
	disableAllActions?: boolean;
	hideOpenWashout?: boolean;
	hideEscalateAction?: boolean;
	disableStatusAction?: boolean;
	disableRowClick?: boolean;
	filterByPayPeriod?: boolean;
	canEditWashout?: boolean;
	statusActionTooltipText?: string;
	tableProps?: Omit<
		TChangesTableProps,
		"data" | "isLoading" | "skeletonRows" | "onRowDeleted" | "onRowSelected" | "setSelectedRowId" | "selectedRowId" | "disableRowClick"
	>;
	onRowClick?: (row: IGetChangeRequest) => void;
};

export const ChangeRequestContainer = (props: Readonly<IChangeRequestTableProps>) => {
	const {
		commissionCycleId,
		locationId,
		payPeriodId,
		escalated,
		origin = "",
		userId = "",
		position = "",
		onRowClick,
		tableProps,
		skeletonRows: externalSkeletonRows,
		disableChat = false,
		disableAllActions,
		hideOpenWashout,
		hideEscalateAction = false,
		disableStatusAction,
		statusActionTooltipText,
		disableRowClick = false,
		filterByPayPeriod = true,
		canEditWashout = false,
	} = props;
	const { enqueueSnackbar } = useSnackbar();
	const queryClient = useQueryClient();
	const [mutationAction, setMutationAction] = useState("");
	const [isChatOpen, setIsChatOpen] = useState(false);
	const [isChatVisible, setIsChatVisible] = useState(false);
	const [skeletonRows, setSkeletonRows] = useState<string[]>([]);
	const [selectedRow, setSelectedRow] = useState<IGetChangeRequest | null>(null);
	const selectedRowId = selectedRow?._id ?? null;

	const { data: requestsData, isLoading } = useChangeRequestsData({
		locationId,
		commissionCycleId,
		filter: {
			userId,
			origin,
			position,
			supportAssigned: escalated,
			payPeriodId: filterByPayPeriod ? payPeriodId : undefined,
		},
	});

	const { requests: data } = requestsData ?? { requests: [] };

	const mergedSkeletonRows = useMemo(() => [...(externalSkeletonRows ?? []), ...skeletonRows], [externalSkeletonRows, skeletonRows]);

	const { mutate: updateChangeRequestMutate, isPending } = useMutation({
		mutationFn: async ({ input, action, files = [] }: { input: IChangeRequestUpdateInput; action: string; files?: File[] }) => {
			const { locationId, commissionCycleId, payPeriodId, requestId } = input;
			setMutationAction(action);
			const newInput = {
				...input,
			};
			if (files.length) {
				const uploadPromises = [];
				const uploadFilesNames: string[] = [];
				for (const file of files) {
					const presigned = await getChatAttachmentsPresignedUrl({
						locationId,
						commissionCycleId,
						payPeriodId,
						requestId,
						file,
					});
					if (presigned) {
						const fileName = presigned?.fileName;
						const uploadFile = new File([file], fileName);
						const options = {
							headers: {
								"Content-Type": uploadFile.type,
							},
						};
						const promise = apiInstance.put(presigned?.presignedUrl, uploadFile, options);
						uploadFilesNames.push(fileName);
						uploadPromises.push(promise);
					}
				}
				await Promise.all(uploadPromises);
				set(
					newInput,
					"input.chat.attachments",
					files.map((file, index) => ({
						name: uploadFilesNames[index],
						displayName: file.name,
						type: file.type,
						size: file.size,
					}))
				);
			}
			return updateChangeRequest(newInput);
		},
		onSuccess: (params: { data: { updateChangeRequest: IGetChangeRequestsByCommissionCycle } }, { input, action }) => {
			setMutationAction("");
			const newData = params?.data;
			if (newData) {
				queryClient.setQueryData(
					["changeRequests", commissionCycleId, payPeriodId ?? "", position ?? "", userId ?? "", origin ?? "", escalated ?? ""],
					(oldData: IGetChangeRequestsByCommissionCycle) => changeRequestSetQueryDataCallback(action, oldData, newData)
				);
			}
			setSkeletonRows((prev) => prev.filter((rowId) => rowId !== input.requestId));
		},
		onError: (error, { input }) => {
			setMutationAction("");
			createErrorToast(enqueueSnackbar, error.message);
			setSkeletonRows((prev) => prev.filter((rowId) => rowId !== input.requestId));
		},
	});

	const handleUpdate = (input: IInput, action: string, files?: File[]) => {
		const requestId = selectedRowId ?? "";
		setSkeletonRows((prev) => [...prev, requestId]);

		const newInput: IChangeRequestUpdateInput = {
			locationId,
			requestId,
			input,
			commissionCycleId: selectedRow?.commissionCycleId ?? "",
			payPeriodId: selectedRow?.requestPayPeriodId ?? payPeriodId,
			userId: selectedRow?.user._id ?? userId,
			position: selectedRow?.position ?? position,
		};
		updateChangeRequestMutate({ action, files, input: newInput });
	};

	const handleDelete = useCallback(async (rows: IGetChangeRequest[]) => {
		if (!rows.length) {
			return true;
		}
		setSkeletonRows((prev) => [...prev, ...rows.map((row) => row._id)]);
		for (const element of rows) {
			const input: IChangeRequestUpdateInput = {
				locationId,
				commissionCycleId: element.commissionCycleId,
				payPeriodId: element.requestPayPeriodId,
				userId: element.user._id,
				requestId: element._id,
				position: element.position,
				input: {
					status: "DELETED",
				},
			};
			updateChangeRequestMutate({ action: "DELETE", input });
		}
		return true;
	}, []);

	const currentUserId = useSelector((state: RootState) => state.user.user?._id);

	const isRowSelectable = useMemo(() => {
		if (tableProps?.allowDeletion) {
			return (changeRequest: IGetChangeRequest) => canDelete({ selectedRowId, row: changeRequest, userId: currentUserId });
		}
		return () => false;
	}, [selectedRowId, currentUserId, tableProps?.allowDeletion]);

	const handleRowClick = useCallback<Dispatch<SetStateAction<string | null>>>(
		(newRowId) => {
			let rowId: string | null;
			if (typeof newRowId === "function") {
				rowId = newRowId(selectedRowId);
			} else {
				rowId = newRowId;
			}
			const row = data?.find((r: { _id: string }) => r._id === rowId) ?? null;
			setSelectedRow(row);
			setIsChatVisible(true);
			setIsChatOpen(true);
			row && onRowClick?.(row);
		},
		[data, selectedRowId, onRowClick]
	);

	const closeChat = () => {
		setIsChatOpen(false);
		setIsChatVisible(false);
		setSelectedRow(null);
	};

	const toggleOpenChat = () => {
		setIsChatOpen((prev) => !prev);
	};

	useEffect(() => {
		setSelectedRow((prev) => {
			const result = data?.find((request: { _id: string }) => request?._id === prev?._id) ?? null;
			if (!result) {
				closeChat();
			}
			return result;
		});
	}, [data]);

	return (
		<>
			<ChangeRequestTable
				data={data ?? []}
				isLoading={isLoading}
				{...tableProps}
				allowDeletion={tableProps?.allowDeletion ?? false}
				skeletonRows={mergedSkeletonRows}
				onRowDeleted={handleDelete}
				selectedRowId={selectedRowId}
				setSelectedRowId={handleRowClick}
				isRowSelectable={isRowSelectable}
				disableRowClick={disableRowClick}
			/>
			{!disableChat && (
				<ChangeRequestChat
					selectedRowId={selectedRowId}
					isVisible={isChatVisible}
					isEscalated={!!selectedRow?.supportAssigned}
					isOpen={isChatOpen}
					closeChat={closeChat}
					toggleOpenChat={toggleOpenChat}
					messages={selectedRow?.chat || []}
					title={selectedRow?.issueSummary ?? ""}
					hideOpenWashout={hideOpenWashout ?? selectedRow?.payPeriod._id === payPeriodId}
					disableAllActions={disableAllActions ?? selectedRow?.status !== "PENDING"}
					hideEscalateAction={hideEscalateAction}
					disableStatusAction={disableStatusAction ?? !!selectedRow?.supportAssigned}
					userId={selectedRow?.user._id ?? userId}
					locationId={locationId}
					commissionCycleId={selectedRow?.commissionCycleId ?? commissionCycleId}
					payPeriodId={selectedRow?.payPeriod._id ?? payPeriodId}
					position={selectedRow?.position ?? position}
					handleUpdate={handleUpdate}
					pending={{ isPending, mutationAction }}
					statusActionTooltipText={selectedRow?.supportAssigned ? statusActionTooltipText : undefined}
					canEditWashout={selectedRow?.origin === "REVIEW" && selectedRow?.status === "PENDING" && canEditWashout}
					selectedRow={selectedRow}
				/>
			)}
		</>
	);
};
